<template>
  <section class="project-type-picker-section">
    <div class="project-type-picker-inner">
      <header class="project-type-header">
        <v-btn
          class="project-type-back"
          variant="text"
          size="default"
          rounded="lg"
          @click="$emit('back')"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <h1 class="project-type-picker-title">¿Qué tipo de proyecto vas a crear?</h1>
        <p class="project-type-picker-subtitle">Elige una opción para continuar</p>
      </header>
      <div class="project-type-grid">
        <button
          v-for="card in cards"
          :key="card.type"
          type="button"
          class="project-type-card"
          @click="$emit('select', card.type)"
        >
          <v-icon class="project-type-card-icon" color="primary">{{ card.icon }}</v-icon>
          <span class="project-type-card-label">{{ card.label }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { ProjectTypeValue } from '@/constants/projectTypes'
import type { HomeProjectTypeCard } from '../../types/home.types'

export default defineComponent({
  name: 'HomeProjectTypePicker',

  props: {
    cards: {
      type: Array as PropType<HomeProjectTypeCard[]>,
      required: true,
    },
  },

  emits: {
    back: () => true,
    select: (_type: ProjectTypeValue) => true,
  },
})
</script>

<style scoped>
.project-type-picker-section {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  background: linear-gradient(135deg, #f7f9fb 0%, #ff9f1c 100%);
  padding: 24rem clamp(1.25rem, 4.5vw, 2.25rem) 1.25rem;
  box-sizing: border-box;
}

/* Extra side inset on the narrowest phones */
@media (max-width: 380px) {
  .project-type-picker-section {
    padding: 4rem 1.5rem 1.2rem;
  }
}

.project-type-picker-inner {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.project-type-header {
  flex-shrink: 0;
  margin-bottom: 0.65rem;
}

.project-type-back {
  color: #1d3557 !important;
  font-weight: 600;
  text-transform: none;
  margin-bottom: 0.4rem;
  min-height: auto !important;
  padding: 0 0.5rem 0 0.25rem !important;
  height: auto !important;
}

.project-type-back :deep(.v-btn__content) {
  font-size: 0.875rem;
  gap: 0.2rem;
}

.project-type-picker-title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  color: #1d3557;
  margin: 0 0 0.2rem;
  text-align: center;
}

.project-type-picker-subtitle {
  font-size: 0.8rem;
  line-height: 1.25;
  color: #457b9d;
  text-align: center;
  margin: 0;
}

.project-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  min-height: 0;
}

/* Four in one row from here up; never 3+1 */
@media (min-width: 900px) {
  .project-type-picker-section {
    padding: 4rem clamp(1.5rem, 4vw, 2.5rem) 3rem;
  }

  .project-type-header {
    margin-bottom: 0;
  }

  .project-type-back {
    margin-bottom: 1.5rem;
  }

  .project-type-back :deep(.v-btn__content) {
    font-size: 1rem;
  }

  .project-type-picker-title {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .project-type-picker-subtitle {
    font-size: 1.1rem;
    margin-bottom: 2.5rem;
  }

  .project-type-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.25rem;
  }
}

/* Tablet / two-by-two, roomier than phone */
@media (min-width: 600px) and (max-width: 899px) {
  .project-type-picker-section {
    padding: 1.5rem 1.25rem 1.5rem;
  }

  .project-type-header {
    margin-bottom: 0.9rem;
  }

  .project-type-back {
    margin-bottom: 0.5rem;
  }

  .project-type-picker-title {
    font-size: 1.35rem;
  }

  .project-type-picker-subtitle {
    font-size: 0.95rem;
  }

  .project-type-grid {
    gap: 0.85rem;
  }
}

.project-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 0;
  min-width: 0;
  padding: 0.6rem 0.35rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  font: inherit;
  text-align: center;
}

.project-type-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  border-color: rgba(29, 53, 87, 0.2);
}

.project-type-card-icon {
  font-size: 1.6rem !important;
  width: 1.6rem !important;
  height: 1.6rem !important;
}

.project-type-card-label {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.15;
  color: #1d3557;
}

@media (min-width: 600px) and (max-width: 899px) {
  .project-type-card {
    gap: 0.6rem;
    padding: 1.1rem 0.6rem;
    border-radius: 14px;
  }

  .project-type-card-icon {
    font-size: 2.1rem !important;
    width: 2.1rem !important;
    height: 2.1rem !important;
  }

  .project-type-card-label {
    font-size: 0.95rem;
  }
}

@media (min-width: 900px) {
  .project-type-card {
    gap: 1rem;
    padding: 2rem 1.25rem;
    border-radius: 16px;
  }

  .project-type-card-icon {
    font-size: 2.4rem !important;
    width: 2.4rem !important;
    height: 2.4rem !important;
  }

  .project-type-card-label {
    font-size: 1.15rem;
  }
}
</style>
